import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FiPlus, FiSearch } from "react-icons/fi";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import AppContext from "../../context/AppContext";
import { fetchAndStoreUserPermissions } from "../../service/AuthService";
import { ORG_FEATURES_CHANGED_EVENT } from "../../utils/orgFeatures";
import useCallCenterT from "../../hooks/useCallCenterT";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import OrganizationFormModal from "../../components/organizations/OrganizationFormModal";
import OrganizationConfigModal from "../../components/organizations/OrganizationConfigModal";
import OrganizationListTable from "../../components/organizations/OrganizationListTable";
import OrganizationStatsBar from "../../components/organizations/OrganizationStatsBar";
import { getOrganizationDisplayCode } from "../../components/organizations/organizationManagementUtils";
import PaymentProofTemplateForm from "../../components/call-center/PaymentProofTemplateForm";
import PaymentConfirmationConfigForm, {
  buildPaymentConfirmationInitialValues,
  buildPaymentConfirmationPayload,
} from "../../components/call-center/PaymentConfirmationConfigForm";
import WhatsAppConfigForm, {
  buildWhatsAppConfigPayload,
  emptyWhatsAppConfigValues,
  mapWhatsAppConfigResponse,
} from "../../components/call-center/WhatsAppConfigForm";
import TelephonyConfigForm, {
  buildTelephonyConfigPayload,
  emptyTelephonyConfigValues,
  mapTelephonyConfigResponse,
} from "../../components/call-center/TelephonyConfigForm";
import ConfirmDialog from "../../components/call-center/listing/ConfirmDialog";
import ListingEmptyState from "../../components/call-center/listing/ListingEmptyState";
import ListingFilterHeader from "../../components/call-center/listing/ListingFilterHeader";
import ListingPaginationFooter from "../../components/call-center/listing/ListingPaginationFooter";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "../../components/call-center/listing/listingStyles";
import {
  listOrganizations,
  createOrganization,
  updateOrganization,
  deactivateOrganization,
  fetchOrganizationLogo,
  uploadOrganizationLogo,
} from "../../service/CallCenterOrgAdmin";
import {
  getAdminOrganizationPaymentProofTemplate,
  updateAdminOrganizationPaymentProofTemplate,
} from "../../service/CallCenterPaymentProofTemplate";
import {
  getAdminOrganizationPaymentConfirmationConfig,
  testAdminOrganizationPaymentConfirmationConnection,
  updateAdminOrganizationPaymentConfirmationConfig,
} from "../../service/CallCenterPaymentConfirmationConfig";
import {
  getAdminOrganizationWhatsAppConfig,
  updateAdminOrganizationWhatsAppConfig,
} from "../../service/CallCenterWhatsAppConfig";
import {
  getAdminOrganizationTelephonyConfig,
  updateAdminOrganizationTelephonyConfig,
} from "../../service/CallCenterTelephonyConfig";

const CODE_PATTERN = /^[A-Z0-9_-]+$/;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const SORT_BY_OPTIONS = [
  { value: "CODE", labelKey: "ccOrgSortCode" },
  { value: "NAME", labelKey: "ccOrgSortName" },
  { value: "CREATED_AT", labelKey: "ccOrgSortCreated" },
];

const OrganizationManagement = () => {
  const { userType } = useContext(AppContext);
  const t = useCallCenterT();
  const isSuperAdmin = userType === "super_admin";

  const [organizations, setOrganizations] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("CODE");
  const [sortDir, setSortDir] = useState("ASC");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingOrg, setEditingOrg] = useState(null);
  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [formCallingEnabled, setFormCallingEnabled] = useState(true);
  const [formWebCallingEnabled, setFormWebCallingEnabled] = useState(true);
  const [formDesktopCallingEnabled, setFormDesktopCallingEnabled] = useState(true);
  const [formAiCallingEnabled, setFormAiCallingEnabled] = useState(true);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const [templateOrg, setTemplateOrg] = useState(null);
  const [templateData, setTemplateData] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);

  const [paymentConfigOrg, setPaymentConfigOrg] = useState(null);
  const [paymentConfigValues, setPaymentConfigValues] = useState(buildPaymentConfirmationInitialValues());
  const [paymentConfigFlags, setPaymentConfigFlags] = useState({
    webhookSecretConfigured: false,
    pollAuthSecretConfigured: false,
    mqPasswordConfigured: false,
  });
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(false);
  const [paymentConfigSaving, setPaymentConfigSaving] = useState(false);
  const [paymentConfigTesting, setPaymentConfigTesting] = useState(false);

  const [whatsappOrg, setWhatsappOrg] = useState(null);
  const [whatsappValues, setWhatsappValues] = useState(emptyWhatsAppConfigValues);
  const [whatsappFlags, setWhatsappFlags] = useState(mapWhatsAppConfigResponse({}).flags);
  const [whatsappOrganizationId, setWhatsappOrganizationId] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappSaving, setWhatsappSaving] = useState(false);

  const [telephonyOrg, setTelephonyOrg] = useState(null);
  const [telephonyValues, setTelephonyValues] = useState(emptyTelephonyConfigValues);
  const [telephonyFlags, setTelephonyFlags] = useState(mapTelephonyConfigResponse({}).flags);
  const [telephonyLoading, setTelephonyLoading] = useState(false);
  const [telephonySaving, setTelephonySaving] = useState(false);

  const hasActiveFilters = Boolean(search || includeInactive);

  const pageStats = useMemo(() => {
    const activeOnPage = organizations.filter((org) => org.active !== false).length;
    const callingOnPage = organizations.filter((org) => org.callingEnabled !== false).length;
    return {
      activeOnPage,
      callingOnPage,
      pageCount: organizations.length,
    };
  }, [organizations]);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listOrganizations({
        page,
        size: pageSize,
        sortBy,
        sortDir,
        includeInactive,
        q: search,
      });
      setOrganizations(Array.isArray(data?.items) ? data.items : []);
      setTotalElements(typeof data?.totalElements === "number" ? data.totalElements : 0);
      setTotalPages(typeof data?.totalPages === "number" ? data.totalPages : 0);
    } catch (loadError) {
      const envelope = loadError?.envelope ?? loadError?.response?.data;
      const message =
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, t("ccOrgLoadFailed"))
          : t("ccOrgLoadFailed");
      setError(message);
      toast.error(message);
      setOrganizations([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, includeInactive, search, t]);

  useEffect(() => {
    if (isSuperAdmin) {
      loadOrganizations();
    }
  }, [isSuperAdmin, loadOrganizations]);

  useEffect(
    () => () => {
      if (logoPreviewUrl && logoPreviewUrl.startsWith("blob:")) {
        window.URL.revokeObjectURL(logoPreviewUrl);
      }
    },
    [logoPreviewUrl]
  );

  const applyFilters = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setIncludeInactive(false);
    setPage(0);
  };

  const openCreate = () => {
    setFormMode("create");
    setEditingOrg(null);
    setFormCode("");
    setFormName("");
    setFormActive(true);
    setFormCallingEnabled(true);
    setFormWebCallingEnabled(true);
    setFormDesktopCallingEnabled(true);
    setFormAiCallingEnabled(true);
    setLogoPreviewUrl("");
    setFormOpen(true);
  };

  const loadOrganizationLogoPreview = async (organizationId) => {
    if (organizationId == null) {
      setLogoPreviewUrl("");
      return;
    }
    try {
      const blob = await fetchOrganizationLogo(organizationId);
      const objectUrl = window.URL.createObjectURL(blob);
      setLogoPreviewUrl((current) => {
        if (current && current.startsWith("blob:")) {
          window.URL.revokeObjectURL(current);
        }
        return objectUrl;
      });
    } catch {
      setLogoPreviewUrl((current) => {
        if (current && current.startsWith("blob:")) {
          window.URL.revokeObjectURL(current);
        }
        return "";
      });
    }
  };

  const openEdit = (org) => {
    setFormMode("edit");
    setEditingOrg(org);
    setFormCode(org.code || "");
    setFormName(org.name || "");
    setFormActive(org.active !== false);
    setFormCallingEnabled(org.callingEnabled !== false);
    setFormWebCallingEnabled(org.webCallingEnabled !== false);
    setFormDesktopCallingEnabled(org.desktopCallingEnabled !== false);
    setFormAiCallingEnabled(org.aiCallingEnabled !== false);
    setLogoPreviewUrl("");
    setFormOpen(true);
    loadOrganizationLogoPreview(org.id);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || editingOrg?.id == null) return;
    try {
      setLogoUploading(true);
      await uploadOrganizationLogo(editingOrg.id, file);
      toast.success(t("ccOrgLogoUploaded"));
      await loadOrganizationLogoPreview(editingOrg.id);
    } catch (uploadError) {
      toast.error(uploadError?.message || t("ccOrgLogoUploadFailed"));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSave = async () => {
    const code = formCode.trim().toUpperCase();
    const name = formName.trim();
    if (!name) {
      toast.error(t("ccOrgNameRequired"));
      return;
    }
    if (formMode === "create") {
      if (!code) {
        toast.error(t("ccOrgCodeRequired"));
        return;
      }
      if (!CODE_PATTERN.test(code)) {
        toast.error(t("ccOrgCodeInvalid"));
        return;
      }
    }

    setSaving(true);
    try {
      if (formMode === "create") {
        await createOrganization({
          code,
          name,
          callingEnabled: formCallingEnabled,
          webCallingEnabled: formCallingEnabled && formWebCallingEnabled,
          desktopCallingEnabled: formCallingEnabled && formDesktopCallingEnabled,
          aiCallingEnabled: formCallingEnabled && formAiCallingEnabled,
        });
        toast.success(t("ccOrgCreateSuccess"));
      } else if (editingOrg?.id != null) {
        await updateOrganization(editingOrg.id, {
          name,
          active: formActive,
          callingEnabled: formCallingEnabled,
          webCallingEnabled: formCallingEnabled && formWebCallingEnabled,
          desktopCallingEnabled: formCallingEnabled && formDesktopCallingEnabled,
          aiCallingEnabled: formCallingEnabled && formAiCallingEnabled,
        });
        toast.success(t("ccOrgUpdated"));
        const selectedOrgId =
          typeof localStorage !== "undefined" ? localStorage.getItem("selectedOrgId") : null;
        if (selectedOrgId != null && String(editingOrg.id) === String(selectedOrgId)) {
          const token = Cookies.get("accessToken");
          if (token) {
            await fetchAndStoreUserPermissions(token);
            window.dispatchEvent(new CustomEvent(ORG_FEATURES_CHANGED_EVENT));
          }
        }
      }
      setFormOpen(false);
      await loadOrganizations();
    } catch (saveError) {
      const envelope = saveError?.envelope ?? saveError?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, t("ccOrgSaveFailed"))
          : t("ccOrgSaveFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async () => {
    if (deactivateTarget?.id == null) return;
    setDeactivating(true);
    try {
      await deactivateOrganization(deactivateTarget.id);
      toast.success(t("ccOrgDeactivated"));
      setDeactivateTarget(null);
      await loadOrganizations();
    } catch (deactivateError) {
      const envelope = deactivateError?.envelope ?? deactivateError?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, t("ccOrgDeactivateFailed"))
          : t("ccOrgDeactivateFailed")
      );
    } finally {
      setDeactivating(false);
    }
  };

  const openTemplateEditor = async (org) => {
    setTemplateOrg(org);
    setTemplateLoading(true);
    try {
      const response = await getAdminOrganizationPaymentProofTemplate(org.id);
      setTemplateData(response?.template || {});
    } catch (loadError) {
      toast.error(loadError?.message || t("ccOrgTemplateLoadFailed"));
      setTemplateData({});
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleTemplateSave = async (nextTemplate) => {
    if (templateOrg?.id == null) return;
    setTemplateSaving(true);
    try {
      const response = await updateAdminOrganizationPaymentProofTemplate(templateOrg.id, nextTemplate);
      setTemplateData(response?.template || nextTemplate);
      toast.success(t("ccOrgTemplateSaved"));
    } catch (saveError) {
      toast.error(saveError?.message || t("ccOrgTemplateSaveFailed"));
    } finally {
      setTemplateSaving(false);
    }
  };

  const openPaymentConfigEditor = async (org) => {
    setPaymentConfigOrg(org);
    setPaymentConfigLoading(true);
    try {
      const data = await getAdminOrganizationPaymentConfirmationConfig(org.id);
      setPaymentConfigValues(buildPaymentConfirmationInitialValues(data));
      setPaymentConfigFlags({
        webhookSecretConfigured: Boolean(data?.webhookSecretConfigured),
        pollAuthSecretConfigured: Boolean(data?.pollAuthSecretConfigured),
        mqPasswordConfigured: Boolean(data?.mqPasswordConfigured),
      });
    } catch (loadError) {
      toast.error(loadError?.message || t("ccPaymentConfirmationLoadFailed"));
      setPaymentConfigValues(buildPaymentConfirmationInitialValues());
    } finally {
      setPaymentConfigLoading(false);
    }
  };

  const handlePaymentConfigSave = async (values) => {
    if (paymentConfigOrg?.id == null) return;
    setPaymentConfigSaving(true);
    try {
      const payload = buildPaymentConfirmationPayload(values);
      const updated = await updateAdminOrganizationPaymentConfirmationConfig(
        paymentConfigOrg.id,
        payload
      );
      setPaymentConfigFlags({
        webhookSecretConfigured: Boolean(updated?.webhookSecretConfigured),
        pollAuthSecretConfigured: Boolean(updated?.pollAuthSecretConfigured),
        mqPasswordConfigured: Boolean(updated?.mqPasswordConfigured),
      });
      toast.success(t("ccPaymentConfirmationSaveSuccess"));
    } catch (saveError) {
      toast.error(saveError?.message || t("ccPaymentConfirmationSaveFailed"));
    } finally {
      setPaymentConfigSaving(false);
    }
  };

  const handlePaymentConfigTest = async () => {
    if (paymentConfigOrg?.id == null) return;
    setPaymentConfigTesting(true);
    try {
      const result = await testAdminOrganizationPaymentConfirmationConnection(paymentConfigOrg.id);
      if (result?.success) {
        toast.success(result.message || t("ccPaymentConfirmationTestSuccess"));
      } else {
        toast.error(result?.message || t("ccPaymentConfirmationTestFailed"));
      }
    } catch (testError) {
      toast.error(testError?.message || t("ccPaymentConfirmationTestFailed"));
    } finally {
      setPaymentConfigTesting(false);
    }
  };

  const openWhatsAppEditor = async (org) => {
    setWhatsappOrg(org);
    setWhatsappLoading(true);
    try {
      const data = await getAdminOrganizationWhatsAppConfig(org.id);
      const mapped = mapWhatsAppConfigResponse(data);
      setWhatsappValues(mapped.values);
      setWhatsappFlags(mapped.flags);
      setWhatsappOrganizationId(mapped.organizationId ?? org.id);
    } catch (loadError) {
      toast.error(loadError?.message || t("ccWhatsAppLoadFailed"));
      setWhatsappValues(emptyWhatsAppConfigValues);
      setWhatsappOrganizationId(org.id);
    } finally {
      setWhatsappLoading(false);
    }
  };

  const openTelephonyEditor = async (org) => {
    setTelephonyOrg(org);
    setTelephonyLoading(true);
    try {
      const data = await getAdminOrganizationTelephonyConfig(org.id);
      const mapped = mapTelephonyConfigResponse(data);
      setTelephonyValues(mapped.values);
      setTelephonyFlags(mapped.flags);
    } catch (loadError) {
      toast.error(loadError?.message || t("ccTelephonyLoadFailed"));
      setTelephonyValues(emptyTelephonyConfigValues);
    } finally {
      setTelephonyLoading(false);
    }
  };

  const handleTelephonySave = async (values, { setSubmitting }) => {
    if (telephonyOrg?.id == null) return;
    setTelephonySaving(true);
    try {
      const updated = await updateAdminOrganizationTelephonyConfig(
        telephonyOrg.id,
        buildTelephonyConfigPayload(values)
      );
      const mapped = mapTelephonyConfigResponse(updated);
      setTelephonyValues(mapped.values);
      setTelephonyFlags(mapped.flags);
      toast.success(t("ccTelephonySaveSuccess"));
      loadOrganizations();
    } catch (saveError) {
      toast.error(saveError?.message || t("ccTelephonySaveFailed"));
    } finally {
      setTelephonySaving(false);
      setSubmitting(false);
    }
  };

  const handleWhatsAppSave = async (values, { setSubmitting }) => {
    if (whatsappOrg?.id == null) return;
    setWhatsappSaving(true);
    try {
      const updated = await updateAdminOrganizationWhatsAppConfig(
        whatsappOrg.id,
        buildWhatsAppConfigPayload(values)
      );
      const mapped = mapWhatsAppConfigResponse(updated);
      setWhatsappFlags(mapped.flags);
      setWhatsappOrganizationId(mapped.organizationId ?? whatsappOrg.id);
      toast.success(t("ccWhatsAppSaveSuccess"));
    } catch (saveError) {
      toast.error(saveError?.message || t("ccWhatsAppSaveFailed"));
    } finally {
      setWhatsappSaving(false);
      setSubmitting(false);
    }
  };

  const formatTimestamp = (value) => {
    if (!value) return t("ccEmDash");
    try {
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(value);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className={LISTING_PAGE_CLASS}>
        <div className={`${LISTING_CONTAINER_CLASS} max-w-2xl`}>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5 text-sm leading-relaxed text-amber-950 shadow-sm">
            {t("ccOrgAccessDenied")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">{t("ccOrgPageTitle")}</h1>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{t("ccOrgPageSubtitle")}</p>
            </div>
            <button type="button" onClick={openCreate} className={LISTING_PRIMARY_BUTTON_CLASS}>
              <FiPlus className="h-4 w-4" />
              {t("ccOrgCreate")}
            </button>
          </div>
        </header>

        {!loading && organizations.length > 0 ? (
          <OrganizationStatsBar
            totalElements={totalElements}
            activeOnPage={pageStats.activeOnPage}
            callingOnPage={pageStats.callingOnPage}
            pageCount={pageStats.pageCount}
            t={t}
          />
        ) : null}

        {error ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{error}</span>
            <button type="button" onClick={loadOrganizations} className="font-semibold hover:underline">
              {t("ccRefresh")}
            </button>
          </div>
        ) : null}

        <section className={LISTING_FILTER_CARD_CLASS}>
          <ListingFilterHeader
            title={t("ccListFiltersTitle")}
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            resultsLabel={t("ccOrgResultsLabel")}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={loading}
            onRefresh={loadOrganizations}
            actions={
              hasActiveFilters ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  {t("ccClearFilters")}
                </button>
              ) : null
            }
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-8">
              <label className={FILTER_LABEL_CLASS}>{t("ccSearch")}</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") applyFilters();
                    }}
                    placeholder={t("ccOrgSearchPlaceholder")}
                    className={`${FILTER_FIELD_CLASS} pl-9`}
                  />
                </div>
                <button type="button" onClick={applyFilters} className={LISTING_PRIMARY_BUTTON_CLASS}>
                  {t("ccSearch")}
                </button>
              </div>
            </div>
            <div className="md:col-span-4 flex flex-col justify-end">
              <label className={FILTER_LABEL_CLASS}>{t("ccStatus")}</label>
              <label className={`${FILTER_FIELD_CLASS} inline-flex items-center gap-2.5`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF]"
                  checked={includeInactive}
                  onChange={(event) => {
                    setIncludeInactive(event.target.checked);
                    setPage(0);
                  }}
                />
                <span className="text-sm text-slate-700">{t("ccOrgIncludeInactive")}</span>
              </label>
            </div>
            <div className="md:col-span-3">
              <label className={FILTER_LABEL_CLASS}>{t("ccOrgSortBy")}</label>
              <select
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event.target.value);
                  setPage(0);
                }}
                className={FILTER_FIELD_CLASS}
              >
                {SORT_BY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className={FILTER_LABEL_CLASS}>{t("ccOrgSortDirection")}</label>
              <select
                value={sortDir}
                onChange={(event) => {
                  setSortDir(event.target.value);
                  setPage(0);
                }}
                className={FILTER_FIELD_CLASS}
              >
                <option value="ASC">{t("ccOrgSortAsc")}</option>
                <option value="DESC">{t("ccOrgSortDesc")}</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className={FILTER_LABEL_CLASS}>{t("ccOrgPageSize")}</label>
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(0);
                }}
                className={FILTER_FIELD_CLASS}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} {t("ccOrgPerPage")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className={`${LISTING_CARD_CLASS} mt-6 overflow-hidden`}>
          {loading ? (
            <div className="p-4">
              <TableSkeleton columns={7} rows={6} />
            </div>
          ) : !organizations.length ? (
            <ListingEmptyState
              icon={FiPlus}
              title={hasActiveFilters ? t("ccOrgEmptyFilteredTitle") : t("ccOrgEmptyTitle")}
              hint={hasActiveFilters ? t("ccOrgEmptyFilteredHint") : t("ccOrgEmptyHint")}
              action={
                hasActiveFilters ? (
                  <button type="button" onClick={resetFilters} className={`mt-4 ${LISTING_PRIMARY_BUTTON_CLASS}`}>
                    {t("ccClearFilters")}
                  </button>
                ) : (
                  <button type="button" onClick={openCreate} className={`mt-4 ${LISTING_PRIMARY_BUTTON_CLASS}`}>
                    <FiPlus className="h-4 w-4" />
                    {t("ccOrgCreate")}
                  </button>
                )
              }
            />
          ) : (
            <>
              <OrganizationListTable
                organizations={organizations}
                t={t}
                formatTimestamp={formatTimestamp}
                onEdit={openEdit}
                onDeactivate={setDeactivateTarget}
                onOpenTemplate={openTemplateEditor}
                onOpenPayment={openPaymentConfigEditor}
                onOpenTelephony={openTelephonyEditor}
                onOpenWhatsApp={openWhatsAppEditor}
              />
              <ListingPaginationFooter
                totalElements={totalElements}
                resultsLabel={t("ccOrgResultsLabel")}
                page={page}
                totalPages={totalPages}
                loading={loading}
                onPrevious={() => setPage((current) => Math.max(current - 1, 0))}
                onNext={() => setPage((current) => current + 1)}
                previousLabel={t("ccPrevious")}
                nextLabel={t("ccNext")}
              />
            </>
          )}
        </section>
      </div>

      <OrganizationFormModal
        open={formOpen}
        mode={formMode}
        formCode={formCode}
        formName={formName}
        formActive={formActive}
        formCallingEnabled={formCallingEnabled}
        formWebCallingEnabled={formWebCallingEnabled}
        formDesktopCallingEnabled={formDesktopCallingEnabled}
        formAiCallingEnabled={formAiCallingEnabled}
        logoPreviewUrl={logoPreviewUrl}
        logoUploading={logoUploading}
        saving={saving}
        onClose={() => !saving && setFormOpen(false)}
        onSave={handleSave}
        onFormCodeChange={setFormCode}
        onFormNameChange={setFormName}
        onFormActiveChange={setFormActive}
        onFormCallingEnabledChange={(enabled) => {
          setFormCallingEnabled(enabled);
          if (!enabled) {
            setFormWebCallingEnabled(false);
            setFormDesktopCallingEnabled(false);
            setFormAiCallingEnabled(false);
          }
        }}
        onFormWebCallingEnabledChange={setFormWebCallingEnabled}
        onFormDesktopCallingEnabledChange={setFormDesktopCallingEnabled}
        onFormAiCallingEnabledChange={setFormAiCallingEnabled}
        onLogoUpload={handleLogoUpload}
        telephonyProvider={editingOrg?.telephonyProvider}
        t={t}
      />

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title={t("ccOrgDeactivateTitle")}
        message={t("ccOrgDeactivateConfirm").replace("{name}", deactivateTarget?.name || "")}
        confirmLabel={t("ccOrgDeactivate")}
        cancelLabel={t("ccCancel")}
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => !deactivating && setDeactivateTarget(null)}
      />

      <OrganizationConfigModal
        open={Boolean(templateOrg)}
        title={t("ccOrgPaymentProofTemplate")}
        subtitle={templateOrg?.name}
        organizationCode={getOrganizationDisplayCode(templateOrg?.code)}
        onClose={() => !templateSaving && setTemplateOrg(null)}
        disabled={templateSaving}
        closeLabel={t("ccClose")}
        loading={templateLoading}
      >
        <PaymentProofTemplateForm
          initialTemplate={templateData}
          onSubmit={handleTemplateSave}
          onTemplateUpdated={(nextTemplate) =>
            setTemplateData((current) => ({ ...current, ...nextTemplate }))
          }
          saving={templateSaving}
          organizationId={templateOrg?.id}
        />
      </OrganizationConfigModal>

      <OrganizationConfigModal
        open={Boolean(paymentConfigOrg)}
        title={t("ccOrgPaymentConfirmation")}
        subtitle={paymentConfigOrg?.name}
        organizationCode={getOrganizationDisplayCode(paymentConfigOrg?.code)}
        onClose={() => !paymentConfigSaving && setPaymentConfigOrg(null)}
        disabled={paymentConfigSaving}
        closeLabel={t("ccClose")}
        loading={paymentConfigLoading}
      >
        <PaymentConfirmationConfigForm
          t={t}
          initialValues={paymentConfigValues}
          secretFlags={paymentConfigFlags}
          organizationId={paymentConfigOrg?.id}
          saving={paymentConfigSaving}
          onSubmit={handlePaymentConfigSave}
          onTestConnection={handlePaymentConfigTest}
          testingConnection={paymentConfigTesting}
        />
      </OrganizationConfigModal>

      <OrganizationConfigModal
        open={Boolean(telephonyOrg)}
        title={t("ccOrgTelephony")}
        subtitle={telephonyOrg?.name}
        organizationCode={getOrganizationDisplayCode(telephonyOrg?.code)}
        onClose={() => !telephonySaving && setTelephonyOrg(null)}
        disabled={telephonySaving}
        closeLabel={t("ccClose")}
        loading={telephonyLoading}
      >
        <TelephonyConfigForm
          initialValues={telephonyValues}
          flags={telephonyFlags}
          callingEnabled={telephonyOrg?.callingEnabled !== false}
          saving={telephonySaving}
          onSubmit={handleTelephonySave}
          t={t}
        />
      </OrganizationConfigModal>

      <OrganizationConfigModal
        open={Boolean(whatsappOrg)}
        title={t("ccOrgWhatsApp")}
        subtitle={whatsappOrg?.name}
        organizationCode={getOrganizationDisplayCode(whatsappOrg?.code)}
        onClose={() => !whatsappSaving && setWhatsappOrg(null)}
        disabled={whatsappSaving}
        closeLabel={t("ccClose")}
        loading={whatsappLoading}
      >
        <WhatsAppConfigForm
          initialValues={whatsappValues}
          flags={whatsappFlags}
          organizationId={whatsappOrganizationId}
          saving={whatsappSaving}
          onSubmit={handleWhatsAppSave}
          t={t}
        />
      </OrganizationConfigModal>
    </div>
  );
};

export default OrganizationManagement;
